import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { ListActivityEventsResponseSchema } from "@forgepoint/shared";
import type {
  ActivityEvent,
  ActivityEventEntityType,
  ListActivityEventsResponse,
} from "@forgepoint/shared";

interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

const getDefaultLoadingState = (): LoadingState => ({
  isLoading: false,
  error: null,
});

const buildEntityKey = (
  entityType: ActivityEventEntityType,
  entityId: string,
): string => {
  return `${entityType}:${entityId}`;
};

interface ActivityEventsStore {
  events: Map<string, ActivityEvent>;
  eventsByEntity: Map<string, string[]>;
  loadingStates: Map<string, LoadingState>;

  fetchActivityEvents: (
    entityType: ActivityEventEntityType,
    entityId: string,
  ) => Promise<void>;
  getActivityEventsForEntity: (
    entityType: ActivityEventEntityType,
    entityId: string,
  ) => ActivityEvent[];
  getLoadingState: (
    entityType: ActivityEventEntityType,
    entityId?: string,
  ) => LoadingState;
}

export const useActivityEventsStore = create<ActivityEventsStore>(
  (set, get) => ({
    events: new Map(),
    eventsByEntity: new Map(),
    loadingStates: new Map(),

    fetchActivityEvents: async (
      entityType: ActivityEventEntityType,
      entityId: string,
    ) => {
      const entityKey = buildEntityKey(entityType, entityId);

      set((state) => {
        const newLoadingStates = new Map(state.loadingStates);
        newLoadingStates.set(entityKey, {
          isLoading: true,
          error: null,
        });
        return { loadingStates: newLoadingStates };
      });

      try {
        const response = await fetchApi(
          `activity-events?entityType=${entityType}&entityId=${entityId}`,
          "GET",
        );

        if (response.ok && response.data) {
          const parsedData = ListActivityEventsResponseSchema.parse(
            response.data,
          );
          const eventsData: ListActivityEventsResponse = parsedData;

          set((state) => {
            const newEvents = new Map(state.events);
            const newEventsByEntity = new Map(state.eventsByEntity);
            const newLoadingStates = new Map(state.loadingStates);

            const eventIds = eventsData.events.map((event) => {
              newEvents.set(event.id, event);
              return event.id;
            });

            newEventsByEntity.set(entityKey, eventIds);
            newLoadingStates.set(entityKey, {
              isLoading: false,
              error: null,
            });

            return {
              events: newEvents,
              eventsByEntity: newEventsByEntity,
              loadingStates: newLoadingStates,
            };
          });
          return;
        }

        set((state) => {
          const newLoadingStates = new Map(state.loadingStates);
          newLoadingStates.set(entityKey, {
            isLoading: false,
            error: "Failed to fetch activity events",
          });
          return { loadingStates: newLoadingStates };
        });
      } catch (error) {
        captureException(error as Error);
        set((state) => {
          const newLoadingStates = new Map(state.loadingStates);
          newLoadingStates.set(entityKey, {
            isLoading: false,
            error: "Failed to fetch activity events",
          });
          return { loadingStates: newLoadingStates };
        });
      }
    },

    getActivityEventsForEntity: (
      entityType: ActivityEventEntityType,
      entityId: string,
    ) => {
      const entityKey = buildEntityKey(entityType, entityId);
      const eventIds = get().eventsByEntity.get(entityKey) || [];
      const events = get().events;

      return eventIds
        .map((id) => events.get(id))
        .filter((event): event is ActivityEvent => event !== undefined);
    },

    getLoadingState: (
      entityType: ActivityEventEntityType,
      entityId?: string,
    ) => {
      const key = entityId ? buildEntityKey(entityType, entityId) : entityType;
      return get().loadingStates.get(key) || getDefaultLoadingState();
    },
  }),
);
